"use client";

import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";

export interface SearchResultGroupProps {
  /**
   * Title of the group (e.g., "Sites", "Devices", "Contacts")
   */
  title: string;
  /**
   * Number of results in this group (optional, shown in header)
   */
  count?: number;
  /**
   * Whether the group is currently loading
   */
  isLoading?: boolean;
  /**
   * Optional footer content (e.g., "View all" link)
   */
  footer?: ReactNode;
  /**
   * Child result items
   */
  children: ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * SearchResultGroup
 *
 * Groups related search results with a header showing the category and count.
 * Used to organize results from different sources (sites, devices, contacts, etc.).
 *
 * Modern design inspired by Linear, Raycast, and Vercel:
 * - Bold, prominent header for clear section definition
 * - Integrated count in natural text flow
 * - Proper spacing and hierarchy
 * - No redundant icons (results have their own)
 * - Optional footer for "View all" links when more results are available
 *
 * @example
 * ```tsx
 * <SearchResultGroup
 *   title="Sites"
 *   count={3}
 *   footer={
 *     <button onClick={handleViewAll}>
 *       View all 127 sites →
 *     </button>
 *   }
 * >
 *   <SearchResultItem id="1" title="Headquarters" />
 *   <SearchResultItem id="2" title="Warehouse" />
 *   <SearchResultItem id="3" title="Office" />
 * </SearchResultGroup>
 * ```
 */
export function SearchResultGroup({ title, count, isLoading, footer, children, className }: SearchResultGroupProps) {
  return (
    <div className={twMerge("flex flex-col", className)}>
      {/* Group Header - Prominent with visual separation */}
      <div className="flex items-baseline gap-1.5 px-4 pt-4 pb-2 border-t border-border-muted first:border-t-0">
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        {!isLoading && count !== undefined && <span className="text-xs text-text-secondary">{count}</span>}
        {isLoading && <span className="text-xs text-text-secondary animate-pulse">Loading...</span>}
      </div>

      {/* Group Items */}
      <div className="flex flex-col">{children}</div>

      {/* Optional Footer */}
      {footer && <div className="px-4 py-2 border-t border-border-subtle">{footer}</div>}
    </div>
  );
}
